import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Pencil, ShieldCheck, Link2, Users, Settings, ArrowLeftRight } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { short } from "../lib/format";
import { addrUrl } from "../lib/chains";
import { useApp } from "../context/AppContext";
import { Avatar, RelationButtons } from "../components/Members";
import { EditProfileDialog } from "../components/EditProfileDialog";
import { Panel, Loading, ErrorState, Empty } from "../components/State";
import { CreatorEarnings } from "../components/CreatorEarnings";
import { LaunchedTokens } from "../components/LaunchedTokens";

const TokenButtons = ({ address, isMe }) => {
  const [tokens, setTokens] = useState([]);
  useEffect(() => { api.get(`/metadata/owned/${address}`).then((r) => setTokens(r.data.tokens)).catch(() => setTokens([])); }, [address]);
  const multi = tokens.length > 1;
  const cls = "inline-flex items-center gap-1.5 rounded-lg border border-lime/40 px-4 py-2 text-xs font-semibold text-lime transition-colors hover:bg-lime hover:text-black";
  return tokens.map((t) => (
    <span key={t.address} className="contents">
      {isMe && <Link to={`/token/${t.address}/admin`} data-testid={`profile-settings-${t.address}`} className={cls}><Settings className="h-3.5 w-3.5" /> Settings{multi ? ` · ${t.symbol}` : ""}</Link>}
      <Link to={`/token/${t.address}#swap`} data-testid={`profile-swap-${t.address}`} className={cls}><ArrowLeftRight className="h-3.5 w-3.5" /> Swap{multi || !isMe ? ` ${t.symbol}` : ""}</Link>
    </span>
  ));
};

export default function Profile() {
  const { address } = useParams();
  const { user, social } = useApp();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/members/${address}`).then((r) => { setP(r.data); setErr(null); }).catch((e) => setErr(errMsg(e))), [address]);
  useEffect(() => { load(); }, [load, social]);

  if (err) return <div className="max-w-xl mx-auto py-24"><ErrorState message={err} testid="profile-error" /></div>;
  if (!p) return <Loading />;
  const isMe = user?.address === p.address;
  return (
    <div className="mx-auto max-w-[900px] px-4 lg:px-8 py-10 space-y-6" data-testid="profile-page">
      <section className="glass rounded-2xl p-6 sm:p-8 circuit-border">
        <div className="flex flex-col sm:flex-row gap-6 sm:items-center">
          <Avatar url={p.avatar_url} address={p.address} size={104} />
          <div className="flex-1 min-w-0">
            <h1 data-testid="profile-nickname" className="hc-title font-display text-3xl sm:text-4xl font-black flex items-center gap-2 break-all">
              {p.nickname || short(p.address)} {p.is_admin && <ShieldCheck className="h-6 w-6 text-lime" />}
            </h1>
            <a href={addrUrl(p.address)} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs font-mono text-slate-400 hover:text-lime break-all">{p.address} <ExternalLink className="h-3 w-3" /></a>
            <p className="mt-1 flex items-center gap-3 text-[11px] text-slate-500"><span className="flex items-center gap-1"><Users className="h-3 w-3" /> {p.friends_count} friends</span>{p.mutual && <span className="text-lime">Mutual friends</span>}{p.is_blocked && <span className="text-danger">Blocked by you</span>}</p>
          </div>
          {isMe ? <div className="flex flex-wrap gap-2 self-start"><button data-testid="profile-edit-button" onClick={() => setEdit(true)} className="btn-lime px-4 py-2 text-xs"><Pencil className="h-3.5 w-3.5" /> Edit profile</button><TokenButtons address={p.address} isMe /></div>
            : <div className="flex flex-wrap items-center gap-2 self-start">{user ? <RelationButtons address={p.address} size="md" /> : <p data-testid="profile-connect-hint" className="text-xs text-slate-500">Connect your wallet to add friend or block</p>}<TokenButtons address={p.address} /></div>}
        </div>
        <p data-testid="profile-bio" className="mt-6 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{p.bio || <span className="text-slate-600">No bio yet.</span>}</p>
      </section>
      <Panel title="Links" icon={Link2} testid="profile-links">
        {!p.links.length ? <Empty title={isMe ? "Add links with the Edit button" : "No links shared"} /> : (
          <ul className="divide-y divide-white/5">
            {p.links.map((l, i) => (
              <li key={i}><a data-testid={`profile-link-${i}`} href={l.url} target="_blank" rel="noopener noreferrer nofollow ugc" className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-lime/5 group">
                <span className="text-sm font-semibold text-white group-hover:text-lime">{l.label}</span>
                <span className="text-xs font-mono text-slate-500 truncate flex items-center gap-1">{new URL(l.url).hostname} <ExternalLink className="h-3 w-3" /></span>
              </a></li>
            ))}
          </ul>)}
        <p className="px-5 pb-3 pt-1 text-[10px] text-slate-600">User-submitted links are not verified by HIVEVORA. Never connect your wallet to unknown sites.</p>
      </Panel>
      <CreatorEarnings wallet={p.address} isMe={isMe} />
      <LaunchedTokens creator={p.address} isMe={isMe} />
      {isMe && <EditProfileDialog open={edit} onOpenChange={setEdit} profile={p} onSaved={load} />}
    </div>
  );
}
